import { EPIC_POST_PAID_MODAL_ROUTE } from "@api-routes";
import { Loader } from "@common/handleErrorAndLoading/Loader";
import { useFetch } from "@common/hooks/useFetch";
import { TextField } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "@material-ui/icons/Close";
import { specialAlphaNumericRegex } from "@utils/formControls";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { keycloakUtils } from "src/itsf/keycloak/keycloakUtils";
import "./css/modal.scss";
import { Modal } from "./Modal";

interface IPostPaidForm {
    firstname: string;
    lastname: string;
    msisdn: string;
}

interface IParams {
    mobileNumber: string;
    isPostPaidModalOpen: boolean;
    setIsPostPaidModalOpen: (open: boolean) => void;
    close(): void;
}

export const EpicPostPaidModal = (props: IParams) => {
    const { t } = useTranslation(["common", "portin", "glossary", "validation", "customerDetails"]);
    const { mobileNumber, isPostPaidModalOpen, setIsPostPaidModalOpen, close } = props;
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<boolean>(false);
    const [isResultModalOpen, setIsResultModalOpen] = useState<boolean>(false);
    const [result, setResult] = useState<boolean>(false);
    const [modalPostState, { post }] = useFetch();

    const formMethods = useForm<IPostPaidForm>({
        defaultValues: {
            firstname: "",
            lastname: "",
            msisdn: mobileNumber,
        },
    });

    const { errors, register, trigger, getValues } = formMethods;

    const handleValidateFormData = async () => {
        if (await trigger()) {
            setIsConfirmationModalOpen(true);
        }
    };

    const submitData = async () => {
        const firstname: string = getValues("firstname");
        const lastname: string = getValues("lastname");
        const msisdn: string = getValues("msisdn");

        setIsConfirmationModalOpen(false);

        post<boolean>(EPIC_POST_PAID_MODAL_ROUTE, {
            body: {
                firstname: firstname,
                lastname: lastname,
                msisdn: msisdn,
            },
            isSecured: keycloakUtils.isAuthenticated(),
        }).then((response) => {
            setResult(response || false);
            setIsResultModalOpen(true);
        });
    };

    const handleResultModalAction = () => {
        if (result) setIsPostPaidModalOpen(false);
        setIsResultModalOpen(false);
    };

    return (
        <>
            <Dialog fullWidth maxWidth="md" open={isPostPaidModalOpen} onClose={close}>
                <Grid className="dialog-close" container justify="flex-end">
                    <Grid className="dialog-close__icon" item>
                        <IconButton aria-label="close" onClick={close}>
                            <CloseIcon style={{ color: "#ffffff" }} />
                        </IconButton>
                    </Grid>
                </Grid>
                <DialogTitle disableTypography style={{ backgroundColor: "#4a148c" }}>
                    <Grid container justify="center">
                        <Grid item xs={12}>
                            <Typography align="center" style={{ color: "#ffffff" }} variant="h3">
                                {t("portin:postPaidModalTitle")}
                            </Typography>
                        </Grid>
                    </Grid>
                </DialogTitle>
                <DialogContent>
                    <form style={{ overflow: "hidden" }}>
                        <Grid
                            container
                            justify="center"
                            alignItems="center"
                            style={{ overflowY: "hidden", overflowX: "hidden" }}
                        >
                            <Grid container item direction="column" spacing={2} xs={12} md={8}>
                                <Grid item xs={12}>
                                    <Typography color="secondary" variant="body1">
                                        {t("portin:postPaidModalMessage")}
                                    </Typography>
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <TextField
                                        error={Boolean(errors.msisdn?.message)}
                                        helperText={errors.msisdn?.message}
                                        fullWidth
                                        disabled={true}
                                        id="msisdn"
                                        inputRef={register({
                                            required: t("validation:required"),
                                        })}
                                        label={`${t("portin:phoneNumber")} *`}
                                        margin="dense"
                                        name="msisdn"
                                        type="text"
                                    />
                                </Grid>
                                <Grid container item direction={"row"} spacing={2}>
                                    <Grid item sm={6} xs={12}>
                                        <TextField
                                            error={Boolean(errors.firstname)}
                                            helperText={errors.firstname?.message}
                                            id="firstname"
                                            inputRef={formMethods.register({
                                                required: t("validation:required"),
                                                validate: specialAlphaNumericRegex,
                                            })}
                                            label={`${t("customerDetails:firstName")} *`}
                                            margin="dense"
                                            name="firstname"
                                            type="text"
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item sm={6} xs={12}>
                                        <TextField
                                            error={Boolean(errors.lastname)}
                                            helperText={errors.lastname?.message}
                                            id="lastname"
                                            inputRef={formMethods.register({
                                                required: t("validation:required"),
                                                validate: specialAlphaNumericRegex,
                                            })}
                                            label={`${t("customerDetails:surname")} *`}
                                            margin="dense"
                                            name="lastname"
                                            type="text"
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>
                                {modalPostState.error && (
                                    <Grid item xs={12}>
                                        <Typography color="error" variant="body1">
                                            {modalPostState.error.errorMessage}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    </form>
                </DialogContent>
                <DialogActions>
                    <Grid container justify="center" spacing={2}>
                        {modalPostState.isLoading && (
                            <Grid item sm={2} xs={4}>
                                <Loader size={40} />
                            </Grid>
                        )}
                        {!modalPostState.isLoading && (
                            <>
                                <Grid item md={2} sm={3} xs={6}>
                                    <Button
                                        className="xtest_modal_close_button"
                                        fullWidth
                                        size="large"
                                        variant="contained"
                                        onClick={close}
                                    >
                                        {t("common:close")}
                                    </Button>
                                </Grid>
                                <Grid item md={2} sm={3} xs={6}>
                                    <Button
                                        className="xtest_modal_confirm_button"
                                        color="secondary"
                                        fullWidth
                                        size="large"
                                        variant="contained"
                                        onClick={() => handleValidateFormData()}
                                    >
                                        {t("common:submit")}
                                    </Button>
                                </Grid>
                            </>
                        )}
                    </Grid>
                </DialogActions>
            </Dialog>
            <Modal
                close={() => setIsConfirmationModalOpen(false)}
                confirm={() => {
                    submitData();
                }}
                confirmLabel={t("portin:consent")}
                displayCancel={true}
                content={t("portin:postPaidModalSubmit")}
                isModalOpen={isConfirmationModalOpen}
                title=""
            />
            <Modal
                close={() => handleResultModalAction()}
                confirm={() => handleResultModalAction()}
                confirmLabel={t("common:close")}
                content={result ? t("portin:postPaidResultSuccess") : t("portin:postPaidResultFailed")}
                isModalOpen={isResultModalOpen}
                title=""
            />
        </>
    );
};
